import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";

// Auth is via httpOnly cookies (XSS-hardened) — ensure every request sends
// credentials. No JWT is read from JS storage.
axios.defaults.withCredentials = true;

// Broadcast a session-superseded event when the backend reports this account
// was used on another device (single-session anti-sharing). AuthContext logs
// out + shows a notice.
const _flexxSuperseded = (detail) =>
  typeof detail === "string" && /another device/i.test(detail);

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && _flexxSuperseded(err.response.data?.detail)) {
      window.dispatchEvent(new CustomEvent("flexx:session-superseded"));
    }
    return Promise.reject(err);
  },
);

// Default fetch() to include cookies unless a caller explicitly opts out, and
// detect superseded-session 401s on fetch calls too.
const _flexxOrigFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const opts = init || {};
  const finalOpts = opts.credentials === undefined ? { ...opts, credentials: "include" } : opts;
  const res = await _flexxOrigFetch(input, finalOpts);
  if (res.status === 401) {
    try {
      const body = await res.clone().json();
      if (_flexxSuperseded(body?.detail)) window.dispatchEvent(new CustomEvent("flexx:session-superseded"));
    } catch (_) {}
  }
  return res;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the FLEXX service worker for PWA / offline support.
// Only enabled in production builds (SW on localhost interferes with CRA HMR).
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch((err) => console.warn("[flexx] service worker registration failed:", err));
  });
  // SW asks the active tab to navigate when a notification is tapped.
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "FLEXX_NAV" && event.data.url) {
      try { window.location.assign(event.data.url); } catch (e) { console.warn("[FLEXX SW NAV] route assign failed:", e?.message || e); }
    }
  });
}
